import styled from "styled-components";
import React, { useState, useEffect, useRef } from "react";
import Axios from "axios";
import { format } from "date-fns";
import { Avatar } from "../../components/avatar/index";
import { IoMdClose, IoMdAdd } from "react-icons/io";
import { useHistory, RouteChildrenProps, Link } from "react-router-dom";
import RequestFilter from "./requestFilter";
import QueryString from "query-string";
import { RequestStatusTag, StatusTag, CategoryTag } from "components/tag/index";
import { SearchInput } from "components/input";
import { Button } from "components/button";
import NewRequestModal from "./newRequestModal";
import { useModalContext } from "components/modal";
import { useQuery } from "react-query";
import Pagination from "components/pagination";
import { ContainerWithSidebar } from "components/layout";
import Table, { useTable } from "components/table";
import { dollarFormatter } from "utils";
import ProgressBar from "components/progressBar";
import useQueryString from "hooks/useQueryString";
import { useTranslation } from 'react-i18next';
import { useAuthContext } from "contexts/auth";
import convertCVD  from "utils/convertCVD";
import { Status } from "components/status";
import { debounce } from "lodash";
import TooltippedElement from "components/TooltippedElement";

export interface Tag {
  id: number;
  label: string;
}

type WorkOrderProps = {
  id: number;
  description: string;
  entry_date: Date;
  appointment_count: number;
  completed_count: number;
  status: { label: string; color: string };
};

export interface List {
  id: number;
  entry_date: Date;
  name: string;
  address: string;
  status: string;
  tags: Tag[];
  link: string;
  children?: WorkOrderProps[];
}
export interface Count {
  status: string;
  count: number;
}
export interface Category {
  link: string;
}

export interface Prop {
  list: List[];
  count: Count[];
  categories: Category[];
}

export interface Query {
  category?: string[];
  status?: string[];
  page?: string[];
}

const RequestLists = React.memo(function RequestLists(props: RouteChildrenProps) {
  const { t } = useTranslation();
  const tBase = "views.requests.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { cvd_type } = useAuthContext();

  const [requestData, setRequestData] = useState<Prop | null>();
  const [initialLoad, setInitialLoad] = useState(true);
  const { match, location } = props;
  const path = match?.path.split("/")[2];
  const [query, setFilter] = useState<Query>({});
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const tableRef = React.useRef<any>();
  const [attention, setAttention] = useState(false);

  const queryString = useQueryString();
  
  // Parse orderBy from URL
  const sortFromUrl = React.useMemo(() => {
    const orderBy = queryString.search.orderBy as string;
    if (orderBy) {
      const [column, order] = orderBy.split(':');
      return { column, order: order || 'ASC' };
    }
    return { column: null, order: null };
  }, [queryString.search.orderBy]);

  const table = useTable({
    url: true,
    sort: sortFromUrl,
    pageIndex: query.page ? parseInt(query.page[0]) : 1,
    onSort: (sort) => {
      const orderBy = sort.column ? `${sort.column}:${sort.order}` : undefined;
      const currentParams = QueryString.parse(location.search);
      const newParams = orderBy 
        ? { ...currentParams, orderBy }
        : { ...currentParams };
      
      if (!orderBy && newParams.orderBy) {
        delete newParams.orderBy;
      }
      
      history.replace({
        search: QueryString.stringify(newParams),
      });
    },
    onPaginate: (page) => {
      const currentParams = QueryString.parse(location.search);
      const newParams = { ...currentParams, page: page.toString() };
      
      history.replace({
        search: QueryString.stringify(newParams),
      });
    },
  });

  const fetchData = useQuery(
    [
      "allRequests",
      initialLoad,
      query,
      attention,
      debouncedSearch,
      location.search,
    ],
    async () => {
      if (!initialLoad) {
        // Extract column and order from orderBy
        const orderByValue = queryString.search.orderBy as string;
        const [column, order] = orderByValue ? orderByValue.split(':') : [undefined, undefined];
        
        return await Axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/v2/company/requests/request-list`,
          {
            params: {
              ...query,
              search: debouncedSearch.trim(),
              filter: "requests",
              attention,
              invoice: queryString.search.invoice,
              orderBy: order,
              orderColumn: column,
            },
          }
        ).then((res) => res.data);
      }
    },
    {
      // enabled: false,
      onSuccess: (data) => {
        setRequestData(data);
        tableRef?.current?.scrollTo(0, 0);
      },
    }
  );

  const { setModal } = useModalContext();

  const history = useHistory();

  const handleRequestClick = (id: number) => {
    history.push(`/requests/${id}/`);
  };

  useEffect(() => {
    let rawString = QueryString.parse(location.search);
    let parsedString: { [key: string]: string[] } = {};
    Object.entries(rawString).forEach(([key, value]) => {
      // Exclude orderBy and invoice as they're handled separately
      if (key !== 'orderBy' && key !== 'invoice' && typeof value === "string") {
        parsedString[key] = value.split("|");
      }
    });
    let queryPage = parsedString.page && +parsedString.page[0];
    if (queryPage !== table.pageIndex)
      table.handlePage((parsedString?.page && +parsedString?.page[0]) || 1);
    setFilter(parsedString);
    setInitialLoad(false);
  }, [location.search]);

  useEffect(() => {
    !initialLoad && updateUrl({ ...query, page: [table.pageIndex.toString()] });
  }, [initialLoad, table.pageIndex]);

  const updateUrl = (query: Query) => {
    let stringifyQuery: { [key: string]: string } = {};

    Object.entries(query).forEach(([key, value]) => {
      stringifyQuery[key] = value.join("|");
    });

    // Preserve orderBy and invoice from current URL
    const currentParams = QueryString.parse(location.search);
    if (currentParams.orderBy) {
      stringifyQuery.orderBy = currentParams.orderBy as string;
    }
    if (currentParams.invoice) {
      stringifyQuery.invoice = currentParams.invoice as string;
    }

    history.replace({
      search: QueryString.stringify(stringifyQuery),
    });
  };

  let selected = query && query.category && query.category[0];

  useEffect(() => {
    const totalPage = Math.ceil(
      (selected
        ? requestData?.count?.find((count) => count.status === selected)
            ?.count || 1
        : requestData?.count?.reduce((prev, next) => prev + next.count, 0) ||
          1) / 20
    );

    table.setPageCount(totalPage);
  }, [selected, requestData?.count]);

  const handleStatusClick = (status: string) => {
    if (status === "showAll") {
      updateUrl({})
    } else {
      updateUrl({ ...query, category: [status], page: ["1"] });
    } 
  };

  const handleStatusToQueryString = (
    element: string,
    status?: string[],
    action?: string
  ) => {
    if (action === "ADD") {
      if (!status) {
        return [element];
      }
      return [...status, element];
    } else if (action === "CLEAR") {
      return;
    } else {
      if (status) {
        status = status.filter((s) => s !== element);
        if (status[0]) {
          return status;
        }
      }
      return;
    }
  };

  const handleCategoryClick = (category: string, action: string) => {
    let updatedStatus = handleStatusToQueryString(
      category,
      query && query.status,
      action
    );
    const { page, status, ...updatedQuery } = query;
    if (updatedStatus) {
      updateUrl({ ...updatedQuery, status: updatedStatus, page: ["1"] });
    } else {
      updateUrl({ ...updatedQuery, page: ["1"] });
    }
  };

  const debouncedSetSearch = useRef(
    debounce((inputValue: string) => {
      updateUrl({ ...query, page: ["1"] });
      setDebouncedSearch(inputValue);
    }, 200)
  ).current;

  const handleDebounce = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    setSearch(inputValue);
    debouncedSetSearch(inputValue);
  };

  const columns = {
    id: {
      label: tr("Request #"),
      width: "5rem",
      minWidth: "5rem",
    },
    status: {
      label: tr("Status"),
      width: "7rem",
      minWidth: "6rem",
      cell: (row: any) => {
        const statusColor = convertCVD(row.status_color, cvd_type);
        return (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          {/* <RequestStatusTag>{tr(row.status)}</RequestStatusTag> */}
          <Status color={statusColor}>{tr(row.status)}</Status>
        </div>
        )
      },
    },
    attention: {
      label: "",
      width: "2rem",
      minWidth: "2rem",
      cell: (row: any) => (
        <>
          {row.attention && <span className="text-red-600 font-bold">!!!</span>}
          {}
        </>
      ),
    },
    name: {
      label: tr("Customer"),
      cell: (row: any) => (
        <TooltippedElement
          message={row.name}
          placement="bottom"
          element={
            <div className="min-w-0 truncate" style={{ maxWidth: "220px" }}>
              <Avatar size="small" showLabel>
                {row.name}
              </Avatar>
            </div>
          }
        />
      ),
      width: "220px",
      minWidth: "120px",
      maxWidth: "220px",
      style: { maxWidth: "220px" },
    },
    link: {
      label: tr("Category"),
      width: "9rem",
      minWidth: "7rem",
      cell: (row: any) => (
        <div style={{ display: "flex", justifyContent: "flex-start" }} className="min-w-0 overflow-hidden">
          <CategoryTag>{row.link || tr("General")}</CategoryTag>
        </div>
      ),
    },
    description: {
      label: tr("Description"),
      cell: (row: any) => (
        <div style={{ display: "flex", justifyContent: "flex-start" }} className="min-w-0 overflow-hidden">
          <TooltippedElement
            message={row.description}
            placement="bottom"
            element={
              <div className="truncate w-full overflow-hidden">
                {row.description}
              </div>
            }
          />
        </div>
      ),
      width: "15em",
      minWidth: "12em",
      maxWidth: "15em",
      style: {
        maxWidth: "15em",
      }
    },
    stats: {
      label: tr("Stats"),
      width: "5rem",
      minWidth: "5rem",
      cell: (row: any) => {
        const max = Math.max(
          row.paid_total || 0,
          row.actual_total || 0,
          row.request_total || 0,
          1
        );
        const reqW = ((row.request_total || 0) / max) * 60;
        const invW = ((row.actual_total || 0) / max) * 60;
        const paidW = ((row.paid_total || 0) / max) * 60;

        return (
          <div className="flex flex-col w-[70px]">
            {reqW !== 0 && (
              <div className="flex items-center">
                <span className="text-[0.6rem] font-bold w-[10px] flex-none leading-none">
                  W
                </span>
                <div
                  className={`rounded h-[3px] bg-red-500`}
                  style={{ width: `${reqW}px` }}
                />
              </div>
            )}
            {invW !== 0 && (
              <div className="flex items-center">
                <span className="text-[0.6rem] font-bold w-[10px] flex-none leading-none">
                  I
                </span>
                <div
                  className={`rounded h-[3px] bg-purple-500`}
                  style={{ width: `${invW}px` }}
                />
              </div>
            )}
            {paidW !== 0 && (
              <div className="flex items-center">
                <span className="text-[0.6rem] font-bold w-[10px] flex-none leading-none">
                  P
                </span>
                <div
                  className={`rounded h-[3px] bg-green-500`}
                  style={{ width: `${paidW}px` }}
                />
              </div>
            )}
          </div>
        );
      },
    },
    manager: {
      label: tr("Manager"),
      width: "7rem",
      minWidth: "6rem",
    },
    address: {
      label: tr("Address"),
      cell: (row: any) => (
        <TooltippedElement
          message={row.address || ""}
          placement="bottom"
          element={
            <div className="truncate min-w-0 block" style={{ maxWidth: "220px" }}>
              {row.address || "—"}
            </div>
          }
        />
      ),
      width: "220px",
      minWidth: "120px",
      maxWidth: "220px",
      style: { maxWidth: "220px" },
    },
    info: {
      label: tr("Info"),
      width: "9rem",
      minWidth: "8rem",
      cell: (row: any) => (
        <div className="flex flex-col">
          <span className="text-[0.6rem] font-bold uppercase text-slate-500">
            {row.appt_date ? tr("Last Appointment") : tr("Request Created")}
          </span>
          <span className="leading-none">
            {row.appt_date
              ? format(new Date(row.appt_date + "T00:00"), "MM/dd/yyyy")
              : format(new Date(row.entry_date), "MM/dd/yyyy hh:mm aa")}
          </span>
        </div>
      ),
    },
  };

  return (
    <ContainerWithSidebar>
      <RequestFilter
        query={query}
        selected={selected}
        count={requestData?.count}
        categories={requestData?.categories}
        {...{ handleStatusClick, handleCategoryClick, attention, setAttention }}
      />
      <RequestListContent>
        <HeaderCon>
          <div className="flex items-center justify-between flex-1">
            <div className="flex items-center">
              <h2>{tr("Requests")}</h2>
              <SearchInput
                search
                placeholder={tr("Search...")}
                style={{ width: "400px", marginLeft: "1rem" }}
                value={search}
                onChange={handleDebounce}
              />
            </div>
            <div className="flex items-center justify-end">
              <Button
                style={{}}
                onClick={() => {
                  history.push("requests/mass-schedule");
                }}
              >
                <IoMdAdd /> {tr("Mass Schedule")}
              </Button>
              <Button
                primary
                style={{ marginLeft: "1rem" }}
                onClick={() =>
                  setModal({
                    component: <NewRequestModal />,
                    label: tr("New Request"),
                  })
                }
              >
                <IoMdAdd /> {tr("Add Request")}
              </Button>
            </div>
          </div>
        </HeaderCon>
        <TableScrollWrap>
          <Table
            {...{ ...table, columns }}
            to={(row) => `/requests/${row.id}`}
            data={requestData?.list}
            fixedColumnWidths
          />
        </TableScrollWrap>
      </RequestListContent>
    </ContainerWithSidebar>
  );
});


const HeaderCon = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const RequestListContent = styled.div`
  padding: 2rem;
  min-width: 0;
`;

const TableScrollWrap = styled.div`
  min-width: 0;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export default RequestLists;